const DEFAULT_MEASUREMENT_ID = 'G-DNZ1P15VNT';
const ANALYTICS_PURPOSE_IDS = ['7', '8', '9', '10'];

let scriptInserted = false;
let initialized = false;
let consentGranted = false;
let routerBound = false;
let cmpListenerAttached = false;
let currentPath: string | null = null;
let lastTrackedPath: string | null = null;

const measurementId = (import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined)?.trim() || DEFAULT_MEASUREMENT_ID;

type LegacyCmpCallback = (payload: unknown, success?: boolean) => void;
type LegacyCmp = (command: string, parameter: unknown, callback: LegacyCmpCallback) => void;
type TcfApi = (command: string, version: number, callback: (data: unknown, success: boolean) => void, parameter?: unknown) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    __cmp?: LegacyCmp;
    __tcfapi?: TcfApi;
  }
}

function ensureGaStub() {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = (...args: unknown[]) => {
      window.dataLayer!.push(args);
    };
  }
}

function insertGaScript() {
  if (typeof window === 'undefined' || scriptInserted || !measurementId) {
    return;
  }

  const existing = document.querySelector(`script[src="https://www.googletagmanager.com/gtag/js?id=${measurementId}"]`);
  if (existing) {
    scriptInserted = true;
    return;
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  script.onload = () => {
    scriptInserted = true;
  };
  script.onerror = () => {
    scriptInserted = false;
  };
  document.head.appendChild(script);
  scriptInserted = true;
}

function initializeGa() {
  if (initialized || !measurementId) {
    return;
  }
  ensureGaStub();
  window.gtag?.('js', new Date());
  window.gtag?.('config', measurementId, {
    send_page_view: false,
  });
  initialized = true;
}

export function isAnalyticsEnabled() {
  return initialized && consentGranted;
}

export function enableAnalytics() {
  if (typeof window === 'undefined' || !measurementId) {
    return;
  }
  consentGranted = true;
  insertGaScript();
  initializeGa();
  const path = currentPath || (typeof window !== 'undefined'
    ? `${window.location.pathname}${window.location.search}`
    : null);
  if (path) {
    trackPageView(path);
    currentPath = path;
    lastTrackedPath = path;
  }
}

export function trackPageView(path: string) {
  if (typeof window === 'undefined' || !measurementId || !consentGranted) {
    return;
  }
  ensureGaStub();
  const pageLocation = window.location.origin + path;
  window.gtag?.('event', 'page_view', {
    page_path: path,
    page_location: pageLocation,
  });
}

export function trackEvent(eventName: string, params: Record<string, unknown> = {}) {
  if (typeof window === 'undefined' || !measurementId || !consentGranted) {
    return;
  }
  ensureGaStub();
  window.gtag?.('event', eventName, params);
}

function parseAnalyticsConsent(payload: unknown): boolean {
  if (typeof payload === 'boolean') {
    return payload;
  }
  if (!payload || typeof payload !== 'object') {
    return false;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data: any = payload;

  if (typeof data.analytics === 'boolean') {
    return data.analytics;
  }
  if (typeof data.statistics === 'boolean') {
    return data.statistics;
  }
  if (typeof data.measurement === 'boolean') {
    return data.measurement;
  }
  if (typeof data.performance === 'boolean') {
    return data.performance;
  }
  if (Array.isArray(data.acceptedCategories)) {
    return data.acceptedCategories.some((category: unknown) =>
      typeof category === 'string' && /analytics|measurement|performance/i.test(category),
    );
  }
  if (Array.isArray(data.categories)) {
    return data.categories.some((category: unknown) =>
      typeof category === 'string' && /analytics|measurement|performance/i.test(category),
    );
  }
  if (typeof data.categories === 'object' && data.categories !== null) {
    return Object.keys(data.categories).some((key) => /analytics|measurement|performance/i.test(key) && !!data.categories[key]);
  }
  if (data?.purposeConsents && typeof data.purposeConsents === 'object') {
    return ANALYTICS_PURPOSE_IDS.some((id) => !!data.purposeConsents[id]);
  }
  if (data?.purpose?.consents && typeof data.purpose.consents === 'object') {
    return ANALYTICS_PURPOSE_IDS.some((id) => !!data.purpose.consents[id]);
  }
  if (data?.consent && typeof data.consent === 'object') {
    return parseAnalyticsConsent(data.consent);
  }
  if (data?.status && typeof data.status === 'string') {
    return /accepted|granted|allow/i.test(data.status);
  }

  return false;
}

function attachGatekeeperListeners() {
  if (cmpListenerAttached || typeof window === 'undefined') {
    return;
  }

  const attemptAttach = () => {
    const tcfApi = window.__tcfapi;
    if (typeof tcfApi === 'function') {
      try {
        tcfApi('addEventListener', 2, (tcData, success) => {
          if (!success) return;
          if (parseAnalyticsConsent(tcData)) {
            enableAnalytics();
          }
        });
        tcfApi('getTCData', 2, (tcData, success) => {
          if (!success) return;
          if (parseAnalyticsConsent(tcData)) {
            enableAnalytics();
          }
        });
        cmpListenerAttached = true;
        return true;
      } catch (err) {
        console.warn('[analytics] Failed to attach __tcfapi listener', err);
      }
    }

    const cmp = window.__cmp;
    if (typeof cmp === 'function') {
      try {
        cmp('addEventListener', 'consentChanged', (consentState: unknown) => {
          if (parseAnalyticsConsent(consentState)) {
            enableAnalytics();
          }
        });
      } catch (err) {
        console.warn('[analytics] Failed to register consentChanged listener', err);
      }
      try {
        cmp('getConsentState', null, (consentState: unknown, success?: boolean) => {
          if (success === false) return;
          if (parseAnalyticsConsent(consentState)) {
            enableAnalytics();
          }
        });
      } catch (err) {
        console.warn('[analytics] Failed to query getConsentState', err);
      }
      cmpListenerAttached = true;
      return true;
    }

    return false;
  };

  if (attemptAttach()) {
    return;
  }

  const interval = window.setInterval(() => {
    if (attemptAttach()) {
      window.clearInterval(interval);
    }
  }, 1000);
}

interface RouterLike {
  subscribe: (listener: (state: { initialized: boolean; location: { pathname: string; search: string } }) => void) => void;
}

export function setupAnalytics(router: RouterLike) {
  if (routerBound || typeof window === 'undefined') {
    return;
  }
  routerBound = true;
  attachGatekeeperListeners();

  router.subscribe((state) => {
    if (!state.initialized) {
      return;
    }
    const nextPath = `${state.location.pathname}${state.location.search}`;
    if (lastTrackedPath === nextPath) {
      return;
    }
    lastTrackedPath = nextPath;
    currentPath = nextPath;
    trackPageView(nextPath);
  });
}

export function manualEnableAnalytics() {
  enableAnalytics();
}

